import { useEffect, useState } from 'react'
import { toNumber } from '@/lib/decimal'
import { formatContractHours } from '@/lib/format'
import { beginPlanningEdit } from '@/lib/planning-refresh'
import { cn } from '@/lib/utils'

interface EditableHoursProps {
  value: string | null
  /** Lege invoer geeft een lege string terug; die betekent "wissen". */
  onCommit: (value: string) => void
  placeholder?: string
  label: string
  title?: string
  disabled?: boolean
  className?: string
  /** Gedempt tonen zolang de waarde niet expliciet gezet is (afgeleide capaciteit). */
  derived?: boolean
  /** Bedragen krijgen de mono-uitlijning van geld in plaats van die van uren. */
  money?: boolean
}

/**
 * Een getal in het raster dat je kunt overtypen. In rust is het gewone tekst,
 * zodat een scherm vol cellen niet als een formulier oogt; pas bij focus wordt
 * het een veld.
 *
 * De ruwe invoer blijft ongemoeid in `draft` staan tot je bevestigt. Zou het veld
 * zijn waarde uit het geformatteerde getal afleiden, dan verdwijnt een komma bij
 * de volgende toetsaanslag en kun je geen half uur invoeren.
 *
 * Committen gebeurt op Enter en op blur, en alleen als de waarde werkelijk
 * veranderde: anders zou langs een raster tabben een reeks schrijfacties geven.
 */
export function EditableHours({
  value,
  onCommit,
  placeholder = '-',
  label,
  title,
  disabled,
  className,
  derived,
  money,
}: EditableHoursProps) {
  const [draft, setDraft] = useState<string | null>(null)
  const open = draft !== null

  // Zolang deze cel openstaat pauzeert de achtergrondverversing van de
  // vooruitblik. Het veld toont je ruwe invoer en niet `value`, dus een
  // binnenkomende waarde zou er ongezien onder schuiven en bij bevestigen alsnog
  // overschreven worden. De draft is leidend tot je bevestigt of afbreekt; het
  // opruimen zit in het effect, zodat de pauze ook opheft als de cel verdwijnt
  // zonder blur.
  useEffect(() => (open ? beginPlanningEdit() : undefined), [open])

  const display = value === null || value === '' ? placeholder : formatContractHours(toNumber(value))
  const shared = cn(money ? 'money' : 'num', 'w-full rounded-sm px-1 py-0.5 text-right', className)

  if (disabled) {
    return <span className={cn(shared, 'text-ink-faint')}>{display}</span>
  }

  if (draft === null) {
    return (
      <button
        type="button"
        aria-label={label}
        title={title}
        onClick={() => setDraft(value === null ? '' : String(toNumber(value)))}
        className={cn(
          shared,
          'transition-colors hover:bg-fill-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
          derived ? 'text-ink-faint' : 'text-ink',
        )}
      >
        {display}
      </button>
    )
  }

  const commit = () => {
    const next = draft.trim().replace(',', '.')
    setDraft(null)
    const changed = '' === next ? value !== null : String(toNumber(next)) !== String(toNumber(value))
    if (changed) onCommit(next)
  }

  return (
    <input
      // autoFocus in plaats van een effect op de waarde: dat laatste selecteerde
      // de inhoud na elke toetsaanslag opnieuw, waardoor je geen twee cijfers
      // achter elkaar kon typen.
      autoFocus
      onFocus={(event) => event.currentTarget.select()}
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          setDraft(null)
        }
      }}
      // Dezelfde breedte als de knop, anders springt de hele kolom bij het openen.
      className={cn(shared, 'border border-accent-500 bg-surface text-ink focus:outline-hidden')}
    />
  )
}
